import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Clock, Facebook, Twitter, Instagram, Linkedin, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import SiteLogo from './SiteLogo';
import { useSettings } from '../../contexts/SettingsContext';
import { useNotifications } from '../../hooks/useNotifications';
import axios from 'axios';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();
  const { settings } = useSettings();
  const notifications = useNotifications();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    try {
      setIsSubmitting(true);
      setSubmitStatus('idle');

      await axios.post('/newsletter/subscribe', {
        email: email.trim(),
        source: 'footer'
      });
      
      setSubmitStatus('success');
      setSubmitMessage(t('footer.subscribeSuccess'));
      notifications.success.newsletterSubscribed();
      setEmail('');
    } catch (error: any) {
      setSubmitStatus('error');
      const errorMessage = error.response?.data?.message || t('footer.subscribeError');
      setSubmitMessage(errorMessage);
      notifications.error.newsletterSubscribe(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerLinks = useMemo(
    () => ({
      company: [
        { name: t('footer.aboutUs'), href: '/sobre' },
        { name: t('footer.mission'), href: '/sobre#missao' },
        { name: t('footer.team'), href: '/sobre#equipe' },
        { name: t('footer.careers'), href: '/sobre#carreiras' }
      ],
      services: [
        { name: t('footer.immigration'), href: '/imigracao' },
        { name: t('footer.visas'), href: '/imigracao#vistos' },
        { name: t('footer.citizenship'), href: '/imigracao#cidadania' },
        { name: t('footer.documentation'), href: '/imigracao#documentos' }
      ],
      resources: [
        { name: t('footer.blog'), href: '/blog' },
        { name: t('footer.guide'), href: '/blog#guia' },
        { name: t('footer.faq'), href: '/faq' },
        { name: t('footer.downloads'), href: '/blog#downloads' }
      ],
      support: [
        { name: t('footer.contact'), href: '/contato' },
        { name: t('footer.support'), href: '/contato#suporte' },
        { name: t('footer.privacy'), href: '/privacidade' },
        { name: t('footer.terms'), href: '/termos' }
      ]
    }),
    [t]
  );

  const socialLinks = [
    { name: 'Facebook', href: settings?.social?.facebook, icon: Facebook },
    { name: 'Twitter', href: settings?.social?.twitter, icon: Twitter },
    { name: 'Instagram', href: settings?.social?.instagram, icon: Instagram },
    { name: 'LinkedIn', href: settings?.social?.linkedin, icon: Linkedin },
    { name: 'WhatsApp', href: settings?.social?.whatsapp, icon: MessageCircle },
  ].filter((s) => !!s.href);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-10 sm:py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <Link to="/" className="mb-6 inline-block">
                <SiteLogo size="md" nameClassName="font-bold text-white" />
              </Link>
              
              <p className="text-gray-300 mb-6 leading-relaxed">{t('footer.tagline')}</p>
              
              <div className="space-y-3">
                {settings?.site?.contact?.email && (
                  <a href={`mailto:${settings.site.contact.email}`} className="flex items-center space-x-3 text-gray-300 hover:text-primary-400 transition-colors">
                    <Mail className="w-5 h-5 text-primary-400 flex-shrink-0" />
                    <span>{settings.site.contact.email}</span>
                  </a>
                )}
                {settings?.site?.contact?.phone && (
                  <a href={`tel:${settings.site.contact.phone.replace(/\s/g, '')}`} className="flex items-center space-x-3 text-gray-300 hover:text-primary-400 transition-colors">
                    <Phone className="w-5 h-5 text-primary-400 flex-shrink-0" />
                    <span>{settings.site.contact.phone}</span>
                  </a>
                )}
                {settings?.site?.contact?.address && (
                  settings?.site?.geo?.latitude && settings?.site?.geo?.longitude ? (
                    <a
                      href={`https://www.google.com/maps?q=${encodeURIComponent(settings.site.geo.latitude + ',' + settings.site.geo.longitude)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-gray-300 hover:text-primary-400 transition-colors"
                    >
                      <MapPin className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span>{settings.site.contact.address}</span>
                    </a>
                  ) : (
                    <div className="flex items-center space-x-3 text-gray-300">
                      <MapPin className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span>{settings.site.contact.address}</span>
                    </div>
                  )
                )}
                {settings?.site?.contact?.businessHours && (
                  <div className="flex items-center space-x-3 text-gray-300 text-sm">
                    <Clock className="w-5 h-5 text-primary-400 flex-shrink-0" />
                    <span>{settings.site.contact.businessHours}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.company')}</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.services')}</h3>
              <ul className="space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.resources')}</h3>
              <ul className="space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-primary-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-gray-800 py-6 sm:py-8">
          <div className="max-w-2xl">
            <h3 className="text-xl font-semibold mb-4">{t('footer.newsletterTitle')}</h3>
            <p className="text-gray-300 mb-6">{t('footer.newsletterDesc')}</p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-gray-400"
                required
                disabled={isSubmitting}
              />
              <button 
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('footer.subscribing')}
                  </>
                ) : (
                  t('footer.subscribe')
                )}
              </button>
            </form>
            
            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-4 flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">{submitMessage}</span>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mt-4 flex items-center gap-2 text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{submitMessage}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-6 sm:py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
            <div className="text-gray-400 text-sm flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                {t('footer.copyright', {
                  year: currentYear,
                  siteName: settings?.site?.name || t('common.siteNameFallback')
                })}
              </span>
              <span className="text-gray-500">·</span>
              <a
                href="https://ricci.solutions"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary-400 transition-colors"
              >
                {t('footer.credits')}
              </a>
            </div>
            
            <div className="flex items-center space-x-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    className="text-gray-400 hover:text-primary-400 transition-colors duration-200"
                    aria-label={social.name}
                    target="_blank" rel="noopener noreferrer"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
